import { useState, useEffect, useCallback } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { debug } from '@/lib/debug';

/**
 * PWA update hook  -  deferred updates.
 * 
 * CRITICAL FIX: Previously used auto-reload which caused the app
 * to reload every time the user switched browser tabs and came back, because:
 * 1. visibilitychange → registration.update() checked for new SW
 * 2. onNeedRefresh() auto-called updateSW(true) → page reload
 * 3. skipWaiting + clientsClaim made the new SW take over immediately
 * 
 * Now: Updates are detected silently and applied on the NEXT natural navigation
 * or page load. No more surprise reloads while the user is working.
 */


/**
 * Throw away everything cached and reload from the network.
 *
 * VA-249: a member was still served the old build after pressing "Update app". The button
 * asked the waiting service worker to take over and then called window.location.reload()
 * as a fallback — but a plain reload on a page a service worker controls is answered FROM
 * that worker's cache. When the handover did not complete, the reload re-served the same
 * JavaScript and CSS, the banner reappeared, and pressing it again did the same thing.
 *
 * A "force reload" flag on reload() is not an option: it is deprecated and ignored by
 * every current browser. The only reliable way to get new bytes is to remove the things
 * answering with the old ones.
 *
 * UNREGISTER FIRST, THEN DELETE. This order was originally the other way round and it
 * failed intermittently in testing: the cache came back immediately after being removed.
 * caches.delete() was not failing — it returned true — the still-active worker was simply
 * recreating the cache it had been part-way through populating. Stopping the writer before
 * clearing what it wrote is what makes this deterministic.
 *
 * index.html is served no-store, so with nothing intercepting, the browser fetches it from
 * the network and its hashed asset names pull the new JavaScript and CSS with it.
 */
export async function hardRefresh(): Promise<void> {
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
    debug.log(`PWA: unregistered ${regs.length} service worker(s)`);
  } catch (e) {
    debug.log("PWA: could not unregister service workers (continuing):", e);
  }

  try {
    if ("caches" in window) {
      // Verify rather than assume. A cache that reappears means something is still
      // writing to it, and one more pass after the worker has gone clears it.
      for (let attempt = 0; attempt < 2; attempt++) {
        const keys = await caches.keys();
        if (keys.length === 0) break;
        await Promise.all(keys.map((k) => caches.delete(k)));
        debug.log(`PWA: cleared ${keys.length} cache store(s) (pass ${attempt + 1})`);
      }
      const left = await caches.keys();
      if (left.length) debug.log("PWA: caches still present, reloading anyway:", left);
    }
  } catch (e) {
    debug.log("PWA: could not clear caches (continuing):", e);
  }

  // replace(), not reload(): this navigation must not become a history entry the member
  // can go "back" into and land on the old page again.
  window.location.replace(window.location.href);
}

let pendingUpdate: (() => void) | null = null;
let updateAvailable = false;

export function usePWAUpdate() {
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    const updateSW = registerSW({
      onNeedRefresh() {
        debug.log('PWA: New version available  -  will apply on next navigation');
        // Store the update function but DON'T auto-reload
        pendingUpdate = () => updateSW(true);
        updateAvailable = true;
        setHasUpdate(true);
      },
      onOfflineReady() {
        debug.log('PWA: App ready to work offline');
      },
      onRegistered(registration) {
        debug.log('PWA: Service worker registered', registration);
        if (!registration) return;

        // Check for updates periodically (every 5 minutes)  -  but NOT on tab switch
        // This is a background check only; it won't reload the page.
        // registration.update() can throw InvalidStateError when the SW is mid-
        // update (e.g. right after a deploy) — that's expected and non-actionable,
        // so swallow it instead of letting it bubble to Sentry.
        const check = (why: string) => {
          debug.log(`PWA: update check (${why})`);
          Promise.resolve(registration.update()).catch((err) => {
            debug.log('PWA: update() rejected (non-fatal):', err);
          });
        };

        // Check straight away. Without this, a member who opens the app right after
        // a deploy runs the previous build for up to five minutes with nothing on
        // screen to say so — which is what Bridget reported: "the user will see old
        // options unless they know to refresh."
        check('on load');

        const interval = setInterval(() => check('periodic'), 5 * 60 * 1000);

        // And when they come back to the tab. This is safe now in a way it was not
        // before: the listener that caused the tab-switch reload cascade was doing so
        // because onNeedRefresh AUTO-RELOADED. It no longer does — it only raises the
        // banner, and the reload happens when the member chooses, or on their next
        // navigation. Checking here costs nothing and closes the window where someone
        // sits on stale code unaware.
        const onVisible = () => { if (document.visibilityState === 'visible') check('tab visible'); };
        document.addEventListener('visibilitychange', onVisible);

        (registration as any).__cleanup = () => {
          clearInterval(interval);
          document.removeEventListener('visibilitychange', onVisible);
        };
      },
      onRegisterError(error) {
        console.error('PWA: Service worker registration error:', error);
      },
    });
  }, []);

  // Apply pending update  -  call this when the user is ready.
  //
  // The graceful path is tried first because it is quicker and keeps the app working
  // offline. If it has not taken over within a few seconds, fall through to clearing
  // everything, which always works. The member presses one button either way.
  const applyUpdate = useCallback(() => {
    if (pendingUpdate) {
      debug.log('PWA: Applying deferred update now');
      pendingUpdate();
      pendingUpdate = null;
      updateAvailable = false;
      // 3s, not 1.5s: skipWaiting plus activation is not instant on a slow phone, and
      // cutting it short throws away a handover that was about to succeed.
      setTimeout(() => {
        debug.log('PWA: handover did not complete — clearing caches and reloading');
        void hardRefresh();
      }, 3000);
    } else {
      // The banner is showing but nothing is waiting. That is the state a member gets
      // stuck in, so go straight to the reliable path rather than reloading into the
      // same cached build again.
      debug.log('PWA: no pending update — clearing caches and reloading');
      void hardRefresh();
    }
  }, []);

  return { hasUpdate, applyUpdate };
}

/**
 * Check if a PWA update is pending (can be called from anywhere).
 */
export function hasPendingPWAUpdate(): boolean {
  return updateAvailable;
}

/**
 * Apply pending PWA update if one exists.
 * Call this during natural page transitions (e.g., sidebar navigation)
 * to seamlessly update without interrupting the user's workflow.
 */
export function applyPendingPWAUpdate(): void {
  if (pendingUpdate) {
    debug.log('PWA: Applying update during navigation');
    pendingUpdate();
    pendingUpdate = null;
    updateAvailable = false;
    // Guarantee the page actually reloads to pick up the new code, even if the
    // service worker's controllerchange-triggered reload doesn't fire (e.g. when
    // skipWaiting resolves before the listener is attached).
    setTimeout(() => window.location.reload(), 1500);
  }
}
